/**
 * <sky-newsletter> wraps a Shopify customer (newsletter) form.
 * Before posting it checks the email and, if it is not valid, shows a warning under the field
 * that says what is wrong (missing @, no domain, bad extension...). The warning clears itself
 * after 5 seconds or as soon as the email is fixed.
 */

const EMAIL = /^[^\s@]+@[^\s@]+\.[\p{L}0-9-]{2,}$/u;
const WARNING_MS = 5000;

class SkyNewsletter extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = /** @type {HTMLInputElement | null} */ (this.querySelector('input[type="email"]'));
    if (!this.form || !this.input) return;

    try {
      this.strings = JSON.parse(this.querySelector('[data-strings]')?.textContent || '{}');
    } catch {
      this.strings = {};
    }

    this.onSubmit = this.onSubmit.bind(this);
    this.onInput = this.onInput.bind(this);
    this.form.addEventListener('submit', this.onSubmit);
    this.input.addEventListener('input', this.onInput);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.input?.removeEventListener('input', this.onInput);
    clearTimeout(this.timer);
  }

  /** @param {SubmitEvent} event */
  onSubmit(event) {
    const value = this.input?.value.trim() ?? '';
    if (EMAIL.test(value)) {
      if (this.input) this.input.value = value;
      return;
    }
    event.preventDefault();
    this.warn(this.explain(value));
  }

  onInput() {
    if (this.warning && EMAIL.test(this.input?.value.trim() ?? '')) this.clearWarning();
  }

  /** @param {string} email */
  explain(email) {
    const s = this.strings;
    if (!email) return s.empty;
    if (!email.includes('@')) return s.noAt;
    const [local, domain = ''] = email.split('@');
    if (!local) return s.localPart;
    if (/\s/.test(local)) return s.spaces;
    if (!domain) return s.domain;
    if (!domain.includes('.')) return s.noDot;
    const tld = domain.split('.').pop() ?? '';
    if (tld.length < 2 || !/^[\p{L}0-9-]+$/u.test(tld)) return s.tld;
    return s.invalid;
  }

  /** @param {string} text */
  warn(text) {
    this.clearWarning();
    // A fresh attempt replaces Shopify's message from the previous post
    this.querySelector('.sky-form-msg')?.remove();

    const warning = document.createElement('p');
    warning.className = 'sky-form-msg sky-form-msg--warning';
    warning.id = `${this.form?.id || 'newsletter'}-warning`;
    warning.setAttribute('role', 'alert');
    warning.innerHTML = this.querySelector('template[data-warning-icon]')?.innerHTML ?? '';
    warning.append(text || '');
    this.querySelector('.sky-newsletter__field')?.after(warning);

    this.input?.setAttribute('aria-invalid', 'true');
    this.input?.setAttribute('aria-describedby', warning.id);
    this.input?.focus();
    this.warning = warning;
    this.timer = setTimeout(() => this.clearWarning(), WARNING_MS);
  }

  clearWarning() {
    clearTimeout(this.timer);
    this.warning?.remove();
    this.warning = null;
    this.input?.removeAttribute('aria-invalid');
    this.input?.removeAttribute('aria-describedby');
  }
}

if (!customElements.get('sky-newsletter')) customElements.define('sky-newsletter', SkyNewsletter);
